import PropTypes from 'prop-types';
import React from 'react';
import ThumbnailRow from './thumbnail_row';
import { seeAllSlidesAds } from '../../../../../utils/ads/cadences';
import {
  standardAdPositionGenerator
} from '../../../../../utils/ads/positionStrings';
import { openerImageTypeToRatio } from '../../../../../utils/images';
import {
  isTwitter, isInstagram, isYoutube,
  isVimeo, isPinterest, isFacebook,
  isSpotify, isSoundcloud, isVideo
} from '../../../../../utils/widgetInspector';
import { isImageSection, isHTMLSection } from '../../../../../utils/sections';
import {map, compact, chunk, first, split} from 'lodash';

const THUMBNAIL_COUNT_IN_ROW = 4;

/*
 * Returns the thumbnail type to be used as class selector for styling each
 * thumbnail. In HTML widget case we attempt to find a suitable font awesome
 * icon, otherwise we revert to either our video or widget placeholder.
 */
const getThumbnailType = (section) => {
  if (!isHTMLSection(section)) {
    return first(split(section.type, ':'));
  }

  const resource = section.content.resource;
  switch (true) {
    case isTwitter(resource): return 'fa fa-twitter';
    case isInstagram(resource): return 'fa fa-instagram';
    case isPinterest(resource): return 'fa fa-pinterest';
    case isSpotify(resource): return 'fa fa-spotify';
    case isSoundcloud(resource): return 'fa fa-soundcloud';
    case isFacebook(resource): return 'fa fa-facebook';
    case isYoutube(resource): return 'fa fa-youtube';
    case isVimeo(resource): return 'fa fa-vimeo';
    case isVideo(resource): return 'video';
    default: return 'widget';
  }
};

const imageSectionThumbnailProps = (section) => {
  const previewImage = section.content.preview_image;
  if (!previewImage) {
    return {};
  }
  const { src, original_width_px, original_height_px } = previewImage;
  return {
    src, original_width_px, original_height_px,
    ratio: original_width_px / original_height_px
  };
};

const toThumbnail = (section) =>
  isImageSection(section)
    ? {
      type: getThumbnailType(section),
      ...imageSectionThumbnailProps(section)
    }
    : {
      type: getThumbnailType(section)
    };

const introSlideThumbnail = (openerImage) => ({
  type: 'image',
  src: openerImage.asset.src.full,
  ratio: openerImageTypeToRatio(openerImage.type)
});

const slideshowThumbnails = ({ slideshowSections }) =>
  map(slideshowSections, toThumbnail);

const withSlideIndex = (thumbnail, slideIndex) =>
  ({ ...thumbnail, slideIndex });

const thumbnails = (props) => map(compact([
  props.openerImage && introSlideThumbnail(props.openerImage),
  ...slideshowThumbnails(props)
]), withSlideIndex);

const createThumbnailRows = (props) => chunk(
  thumbnails(props),
  THUMBNAIL_COUNT_IN_ROW
);

const Thumbnails = ({
  adsState,
  handleThumbnailClick,
  ...props
}) => {
  const thumbnailRows = createThumbnailRows(props);
  const adCadence = seeAllSlidesAds(thumbnailRows);
  const adPositionGenerator = standardAdPositionGenerator();
  return (
    <div className="slide-thumbnails">
      {
        map(thumbnailRows, (thumbnails, rowIndex) => (
          <ThumbnailRow key={rowIndex}
            adsState={adsState}
            handleThumbnailClick={handleThumbnailClick}
            thumbnails={thumbnails}
            adPosition={
              adCadence[rowIndex].hasAd
                ? adPositionGenerator.next().value
                : null
            }
          />
        ))
      }
    </div>
  );
};

export default Thumbnails;

Thumbnails.propTypes = {
  adsState: PropTypes.object.isRequired,
  handleThumbnailClick: PropTypes.func.isRequired,
  slideshowSections: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    content: PropTypes.shape({
      dimensions: PropTypes.shape({
        original_width_px: PropTypes.number.isRequired,
        original_height_px: PropTypes.number.isRequired
      }),
      preview_image: PropTypes.shape({
        src: PropTypes.string.isRequired
      }),
      resource: PropTypes.string
    }).isRequired
  })).isRequired,
  openerImage: PropTypes.shape({
    type: PropTypes.string.isRequired,
    alt_title: PropTypes.string,
    asset: PropTypes.shape({
      src: PropTypes.shape({
        full: PropTypes.string
      }).isRequired
    }).isRequired
  })
};
